import { Box, ButtonBase, Divider } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { DotLegalHeader, DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import ClearIcon from "@mui/icons-material/Clear";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { FilterOption, FilterOptionType, needsRoomForOpenCalendar } from "../filterPopover/FilterPopover.types.ts";
import FilterPopoverSubMenu from "../filterPopover/FilterPopoverSubMenu.tsx";
import SelectFieldSubMenu from "../filterPopover/subMenues/SelectFieldSubMenu.tsx";
import RelatedEntitySubMenu from "../filterPopover/subMenues/RelatedEntitySubMenu.tsx";
import { IdNameWithEntityType } from "./IdNameWithEntityType.ts";
import BooleanSubMenu from "../filterPopover/subMenues/BooleanSubMenu.tsx";
import DateFieldSubMenu, { DateFieldFilterEnum } from "../filterPopover/subMenues/DateFieldSubMenu.tsx";
import AmountFieldSubMenu from "../filterPopover/subMenues/AmountFieldSubMenu.tsx";
import NumberFieldSubMenu from "../filterPopover/subMenues/NumberFieldSubMenu.tsx";
import RecurrenceFieldSubMenu, { RecurrenceFilterEnum } from "../filterPopover/subMenues/RecurrenceFieldSubMenu.tsx";
import TextFieldSubMenu from "../filterPopover/subMenues/TextFieldSubMenu.tsx";
import CurrencySubMenu from "../filterPopover/subMenues/CurrencySubMenu.tsx";
import { IdNamePair } from "../../../customLists/customListView/CustomListView.types.ts";
import { isNullOrWhitespace, setFirstLetterToLowerCase } from "../../stringOperations.ts";
import { GetAmountData, GetBooleanData, GetDateData, GetListData, GetNumberData, GetRecurrenceData } from "../filterPopover/subMenues/SubMenuDataHelper.ts";
import { borderRadius } from "../../../privacyTheme.ts";
import { FilterOperatorUi } from "@common/api/filterTypes.ts";
import FilterBarValueOperatorMenu from "@common/components/filterBar/FilterBarValueOperatorMenu.tsx";
import { PopperPlacementType } from "@mui/material/Popper/BasePopper.types";
import { relativeDateUnitTranslationKeys } from "@common/relativeDateCalculation.ts";
import { RelativeDateDirection } from "../../../annualWheel/RelativeDate.types.ts";

// Renders a from/to filter as a chip label. When only one bound is set, prefer "≥ x" / "≤ x"
// over "x - " or " - x" — both backend bounds are inclusive (>= From, <= To), so the symbols
// match what SQL actually applies.
function formatRangeChip(from: string, to: string): string {
    if (from && to) return `${from} - ${to}`;
    if (from) return `≥ ${from}`;
    if (to) return `≤ ${to}`;
    return "";
}

interface ChipDraft {
    filterOption: FilterOption;
    filterBarData: Record<string, Array<IdNamePair>>;
}

export interface FilterBarValueProps {
    filterOption: FilterOption;
    onRemoveOptionClick: () => void;
    isOpen: boolean;
    onToggleSubmenu: (optionId: string | undefined) => void;
    filterBarData: Record<string, IdNamePair[]>;
    onSave: (updatedOption: FilterOption, filterBarData: Record<string, IdNamePair[]>) => void;
    popoverPlacement?: PopperPlacementType;
    // The chip is on screen despite having no value, because the user emptied it rather than removing it.
    isPinned?: boolean;
}

function FilterBarValue(props: FilterBarValueProps) {
    const containerRef = useRef<HTMLElement>(undefined);
    const submenuInputRef = useRef<HTMLInputElement>(null);
    // DotLegalTextField types its inputRef for a textarea, so the value inputs need their own ref
    // rather than the one the list-based submenus share.
    const submenuTextFieldRef = useRef<HTMLTextAreaElement>();
    const valueButtonRef = useRef<HTMLButtonElement>(null);
    const [isTextTruncated, setIsTextTruncated] = useState(false);

    const { onRemoveOptionClick, isOpen, onToggleSubmenu, onSave, popoverPlacement, isPinned } = props;
    // Start-aligned on purpose: the chip resizes as its value is edited, and a centred popover would
    // slide sideways under the user - clearing a long value moves it by half the width the chip loses.
    const submenuPlacement: PopperPlacementType = popoverPlacement?.startsWith("top") ? "top-start" : "bottom-start";
    const { translateString, translateDate, translateNumber, translateCurrencyNames } = useTranslation();

    // While its submenu is open the chip edits a draft; otherwise it shows what the bar holds. Deriving from props
    // instead of mirroring them into state keeps a parent render at one chip render rather than two, since the
    // page hooks rebuild the option array on every render.
    const [draft, setDraft] = useState<ChipDraft | null>(null);
    const [openPart, setOpenPart] = useState<"value" | "operator" | null>(null);
    const filterOption = isOpen && draft ? draft.filterOption : props.filterOption;
    const filterBarData = isOpen && draft ? draft.filterBarData : props.filterBarData;

    const updateDraft = (changes: Partial<ChipDraft>) => {
        setDraft({ filterOption, filterBarData, ...changes });
    };

    useEffect(() => {
        const typographyElement = valueButtonRef.current?.querySelector(".MuiTypography-root") as HTMLElement | null;
        if (typographyElement) {
            setIsTextTruncated(typographyElement.scrollWidth > typographyElement.clientWidth);
        } else {
            setIsTextTruncated(false);
        }
    }, []);

    // The submenu mounts inside a Popper, so the input only exists once the transition has run
    useEffect(() => {
        if (isOpen && openPart === "value") {
            focusSubmenuInput();
        }
    }, [isOpen, openPart]);

    const focusSubmenuInput = () => {
        setTimeout(() => (submenuInputRef.current ?? submenuTextFieldRef.current)?.focus(), 50);
    };

    const applyFromSubmenu = (value: any) => {
        onSave({ ...filterOption, value: value }, filterBarData);
    };

    const togglePart = (part: "value" | "operator") => {
        if (isOpen && openPart === part) {
            onSave(filterOption, filterBarData);
            return;
        }
        setOpenPart(part);
        if (!isOpen) {
            setDraft(null);
            onToggleSubmenu(filterOption.id);
        }
    };

    // A single-select holds one value, so "any of" and "none of" have nothing to distinguish and the operator
    // segment is left out of the chip entirely.
    const operatorIsApplicable =
        !filterOption.singleSelect &&
        !filterOption.hideFilterOperator &&
        (filterOption.type === FilterOptionType.Currency ||
            filterOption.type === FilterOptionType.SelectField ||
            filterOption.type === FilterOptionType.TextField ||
            filterOption.type === FilterOptionType.RelatedEntityField);

    const getOperatorLabel = () => {
        const isNegative = filterOption.filterOperator === FilterOperatorUi.Negative;
        if (filterOption.type === FilterOptionType.TextField) {
            return isNegative ? translateString("filterOperatorNotContain") : translateString("filterOperatorContain");
        }
        return isNegative ? translateString("filterOperatorNoneOf") : translateString("filterOperatorAnyOf");
    };

    const getFilterBarContent = (): { content: string | React.ReactNode | undefined; hoverContent?: React.ReactNode } => {
        if (filterOption.type === FilterOptionType.TextField && !isNullOrWhitespace(filterOption.value)) {
            return { content: filterOption.value as string };
        }

        if (filterOption.type === FilterOptionType.DateField) {
            const dateFieldValue = GetDateData(filterOption.value);
            let chipValue = "";
            if (dateFieldValue.dateFieldEnum !== undefined && dateFieldValue.dateFieldEnum !== null) {
                if (filterOption.fixedData && filterOption.fixedData.length > 0) {
                    const match = filterOption.fixedData.find((x) => x.id === dateFieldValue.dateFieldEnum!.toString());
                    chipValue = match?.name ?? "";
                } else {
                    chipValue = translateString(setFirstLetterToLowerCase(DateFieldFilterEnum[dateFieldValue.dateFieldEnum].toString()));
                }
            }
            if (dateFieldValue.rollingPeriod) {
                const { direction, value, unit } = dateFieldValue.rollingPeriod;
                const directionText = translateString(direction === RelativeDateDirection.After ? "nextPeriod" : "lastPeriod");
                chipValue = `${directionText} ${translateString(relativeDateUnitTranslationKeys[unit], { count: value })}`;
            }
            if (dateFieldValue.from || dateFieldValue.to) {
                chipValue = formatRangeChip(
                    dateFieldValue.from ? translateDate(dateFieldValue.from) : "",
                    dateFieldValue.to ? translateDate(dateFieldValue.to) : ""
                );
            }

            if (!isNullOrWhitespace(chipValue)) {
                return { content: chipValue };
            }
        }

        if (filterOption.type === FilterOptionType.Boolean) {
            let booleanData = GetBooleanData(filterOption.value);
            if (booleanData) {
                if (filterOption.fixedData && filterOption.fixedData.length > 0) {
                    const match = filterOption.fixedData.find((x) => x.id === booleanData);
                    if (match) {
                        return { content: match.name };
                    }
                }
                return { content: booleanData === "1" ? translateString("yes") : translateString("no") };
            }
        }

        if (filterOption.type === FilterOptionType.Number) {
            const numberFieldValue = GetNumberData(filterOption.value);
            const chipValue = formatRangeChip(
                numberFieldValue.from ? translateNumber(numberFieldValue.from) : "",
                numberFieldValue.to ? translateNumber(numberFieldValue.to) : ""
            );
            if (chipValue) {
                return { content: chipValue };
            }
        }

        if (filterOption.type === FilterOptionType.RecurrenceField) {
            const recurrenceValue = GetRecurrenceData(filterOption.value);
            if (recurrenceValue.recurrenceFilterEnum) {
                const presetLabels: Record<number, string> = {
                    [RecurrenceFilterEnum.OneTime]: translateString("oneTimeActivity"),
                    [RecurrenceFilterEnum.UnderAMonth]: translateString("underAMonth"),
                    [RecurrenceFilterEnum.OneMonth]: translateString("everyMonth"),
                    [RecurrenceFilterEnum.ThreeMonths]: translateString("repertEveryXMonth", { repetition: 3 }),
                    [RecurrenceFilterEnum.SixMonths]: translateString("repertEveryXMonth", { repetition: 6 }),
                    [RecurrenceFilterEnum.TwelveMonths]: translateString("repertEveryXMonth", { repetition: 12 }),
                    [RecurrenceFilterEnum.TwoYears]: translateString("recurEveryXYear", { repetition: 2 }),
                };
                return { content: presetLabels[recurrenceValue.recurrenceFilterEnum] ?? "" };
            }
            const recurrenceChip = formatRangeChip(
                recurrenceValue.from != null ? String(recurrenceValue.from) : "",
                recurrenceValue.to != null ? String(recurrenceValue.to) : ""
            );
            if (recurrenceChip) {
                return { content: recurrenceChip };
            }
        }

        if (filterOption.type === FilterOptionType.Amount) {
            const amountFieldValue = GetAmountData(filterOption.value);
            const hasCurrencyCode = !isNullOrWhitespace(amountFieldValue.currencyCode);
            const suffix = hasCurrencyCode ? ` ${amountFieldValue.currencyCode}` : "";

            const rangePart = formatRangeChip(
                amountFieldValue.from ? translateNumber(amountFieldValue.from) : "",
                amountFieldValue.to ? translateNumber(amountFieldValue.to) : ""
            );

            if (rangePart) {
                return { content: rangePart + suffix };
            }
            if (hasCurrencyCode) {
                return { content: amountFieldValue.currencyCode };
            }
        }

        if (filterOption.type === FilterOptionType.Currency) {
            const currencies = GetListData(filterOption.value);
            if (currencies.length > 0) {
                const translatedCurrencies = currencies.map((currency) => {
                    return { id: currency, name: translateCurrencyNames(currency) };
                });
                return {
                    content: translatedCurrencies.length === 1 ? translatedCurrencies[0].name : translatedCurrencies.length.toString(),
                    hoverContent: translatedCurrencies.length > 1 && (
                        <Box sx={{ padding: 0.5 }}>
                            {translatedCurrencies.map((value, index) => (
                                <Box key={value.id + index}>{value.name}</Box>
                            ))}
                        </Box>
                    ),
                };
            }
        }

        if (filterOption.type === FilterOptionType.SelectField) {
            const selectedValues = GetListData(filterOption.value);
            const filteredValues = getSelectValues(selectedValues, filterOption);

            if (filteredValues && filteredValues.length > 0) {
                return {
                    content: filteredValues.length === 1 ? filteredValues[0].name : filteredValues.length.toString(),
                    hoverContent: filteredValues.length > 1 && (
                        <Box sx={{ padding: 0.5 }}>
                            {filteredValues.map((value, index) => (
                                <Box key={value.id + index}>{value.name}</Box>
                            ))}
                        </Box>
                    ),
                };
            }
        }

        if (filterOption.type === FilterOptionType.RelatedEntityField) {
            const selectedIds = GetListData(filterOption.value);
            const hookData = (filterBarData?.[filterOption.id] ?? []) as Array<IdNamePair>;
            const rows = selectedIds.map((id) => hookData.find((x) => x.id === id)).filter((x): x is IdNamePair => x !== undefined);

            if (rows.length > 0) {
                return {
                    content: rows.length === 1 ? rows[0].name : rows.length.toString(),
                    hoverContent: rows.length > 1 && (
                        <Box sx={{ padding: 0.5 }}>
                            {rows.map((r, i) => (
                                <Box key={r.id + i}>{r.name}</Box>
                            ))}
                        </Box>
                    ),
                };
            }
        }

        return { content: isOpen || isPinned ? "..." : undefined };
    };

    const getSelectValues = (selectValues: Array<string>, option: FilterOption): Array<IdNamePair> => {
        if (option.fixedData && option.fixedData.length > 0) {
            return option.fixedData.filter((x) => selectValues.includes(x.id));
        }

        // Use data from the hook for SelectField without fixedData
        // For custom fields, the response uses GUID as key, so extract it
        let dataKey = option.id;
        if (option.id.startsWith("custom-field-")) {
            dataKey = option.id.replace("custom-field-", "");
        }

        const hookData = filterBarData?.[dataKey];

        return selectValues
            .map((id) => {
                const foundMatch = hookData?.find((x) => x.id === id);
                return foundMatch ? { id: id, name: foundMatch.name } : null;
            })
            .filter((x) => x !== null);
    };

    const getCorrectSubmenu = () => {
        switch (filterOption?.type) {
            case FilterOptionType.SelectField: {
                let dataKey = filterOption.id;
                if (filterOption.id.startsWith("custom-field-")) {
                    dataKey = filterOption.id.replace("custom-field-", "");
                }
                return (
                    <SelectFieldSubMenu
                        filterBarData={filterBarData[dataKey]}
                        submenuInputRef={submenuInputRef}
                        selectedOption={filterOption}
                        onToggleSelect={(ids, data) =>
                            updateDraft({ filterOption: { ...filterOption, value: ids }, filterBarData: { ...filterBarData, [dataKey]: data } })
                        }
                    />
                );
            }
            case FilterOptionType.RelatedEntityField:
                return (
                    <RelatedEntitySubMenu
                        filterBarData={filterBarData[filterOption.id] as Array<IdNameWithEntityType> | undefined}
                        submenuInputRef={submenuInputRef}
                        selectedOption={filterOption}
                        onToggleSelect={(ids, data) =>
                            updateDraft({ filterOption: { ...filterOption, value: ids }, filterBarData: { ...filterBarData, [filterOption.id]: data } })
                        }
                    />
                );
            case FilterOptionType.Boolean:
                return (
                    <BooleanSubMenu
                        submenuInputRef={submenuInputRef}
                        selectedOption={filterOption}
                        onChange={(val) => updateDraft({ filterOption: { ...filterOption, value: val } })}
                    />
                );
            case FilterOptionType.DateField:
                return (
                    <DateFieldSubMenu
                        submenuInputRef={submenuInputRef}
                        selectedOption={filterOption}
                        onChange={(val) => updateDraft({ filterOption: { ...filterOption, value: val } })}
                    />
                );

            case FilterOptionType.Amount:
                return (
                    <AmountFieldSubMenu
                        submenuInputRef={submenuInputRef}
                        selectedOption={filterOption}
                        onChange={(val) => updateDraft({ filterOption: { ...filterOption, value: val } })}
                    />
                );
            case FilterOptionType.Number:
                return (
                    <NumberFieldSubMenu
                        submenuInputRef={submenuTextFieldRef}
                        selectedOption={filterOption}
                        onChange={(val) => updateDraft({ filterOption: { ...filterOption, value: val } })}
                        onApply={(val) => applyFromSubmenu(val)}
                    />
                );

            case FilterOptionType.RecurrenceField:
                return (
                    <RecurrenceFieldSubMenu
                        submenuInputRef={submenuInputRef}
                        selectedOption={filterOption}
                        onChange={(val) => updateDraft({ filterOption: { ...filterOption, value: val } })}
                    />
                );

            case FilterOptionType.TextField:
                return (
                    <TextFieldSubMenu
                        submenuInputRef={submenuTextFieldRef}
                        selectedOption={filterOption}
                        onChange={(val) => updateDraft({ filterOption: { ...filterOption, value: val } })}
                        onApply={(val) => applyFromSubmenu(val)}
                    />
                );

            case FilterOptionType.Currency:
                return (
                    <CurrencySubMenu
                        submenuInputRef={submenuInputRef}
                        selectedOption={filterOption}
                        onChange={(currencies) => updateDraft({ filterOption: { ...filterOption, value: currencies } })}
                    />
                );
        }
    };

    const { content, hoverContent } = getFilterBarContent();
    return content == undefined ? undefined : (
        <>
            <Box
                ref={containerRef}
                sx={(theme) => ({
                    display: "flex",
                    alignItems: "center",
                    border: `1px solid ${theme.palette.grey[400]}`,
                    borderRadius: `${borderRadius}px`,
                    overflow: "hidden",
                })}
            >
                <Box sx={(theme) => ({ padding: "8px 16px" })}>
                    <DotLegalHeader headerStyle={"extraSmall"} marginBottom={0}>
                        {filterOption.label}
                    </DotLegalHeader>
                </Box>

                {operatorIsApplicable && (
                    <>
                        <Divider orientation="vertical" flexItem sx={(theme) => ({ borderColor: theme.palette.grey[400] })} />

                        <ButtonBase
                            onClick={() => togglePart("operator")}
                            sx={(theme) => ({
                                display: "flex",
                                alignItems: "center",
                                alignSelf: "stretch",
                                whiteSpace: "nowrap",
                                color: theme.palette.primary.dark,
                                padding: "0px 16px",
                                "&:hover": {
                                    backgroundColor: theme.palette.grey[200],
                                },
                                "&:active": {
                                    backgroundColor: theme.palette.grey[200],
                                },
                            })}
                        >
                            <DotLegalHeader headerStyle={"extraSmall"} marginBottom={0} fontWeight={400}>
                                {getOperatorLabel()}
                            </DotLegalHeader>
                        </ButtonBase>
                    </>
                )}

                <Divider orientation="vertical" flexItem sx={(theme) => ({ borderColor: theme.palette.grey[400] })} />

                <DotLegalTooltip text={!isOpen && (hoverContent || (isTextTruncated && content))}>
                    <ButtonBase
                        ref={valueButtonRef}
                        onClick={() => togglePart("value")}
                        sx={(theme) => ({
                            display: "flex",
                            alignItems: "center",
                            alignSelf: "stretch",
                            overflow: "hidden",
                            color: theme.palette.primary.dark,
                            padding: "0px 16px",
                            "&:hover": {
                                backgroundColor: theme.palette.grey[200],
                            },
                            "&:active": {
                                backgroundColor: theme.palette.grey[200],
                            },
                            "& > :first-child": {
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                            },
                            "& .MuiTypography-root": {
                                maxWidth: "250px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            },
                        })}
                    >
                        <DotLegalHeader headerStyle={"extraSmall"} marginBottom={0} fontWeight={400}>
                            {content}
                        </DotLegalHeader>
                    </ButtonBase>
                </DotLegalTooltip>

                <Divider orientation="vertical" flexItem sx={(theme) => ({ borderColor: theme.palette.grey[400] })} />

                <DotLegalTooltip text={translateString("removeAll")}>
                    <ButtonBase
                        sx={(theme) => ({
                            display: "flex",
                            alignItems: "center",
                            alignSelf: "stretch",
                            fontSize: "12px",
                            color: theme.palette.primary.dark,
                            textAlign: "center",
                            padding: "0px 6px",
                            borderRadius: `0px ${borderRadius}px ${borderRadius}px 0px`,
                            "&:hover": {
                                backgroundColor: theme.palette.grey[200],
                            },
                            "&:active": {
                                backgroundColor: theme.palette.grey[200],
                            },
                        })}
                        onClick={onRemoveOptionClick}
                    >
                        <ClearIcon fontSize={"small"} />
                    </ButtonBase>
                </DotLegalTooltip>
            </Box>

            {isOpen && openPart === "value" && (
                <FilterPopoverSubMenu
                    open
                    submenuAnchorRef={containerRef.current}
                    handleSubmenuPaperEnter={focusSubmenuInput}
                    placement={submenuPlacement}
                    onBackdropClick={() => onSave(filterOption, filterBarData)}
                    paperSx={needsRoomForOpenCalendar(filterOption) ? { overflow: "visible" } : undefined}
                >
                    {getCorrectSubmenu()}
                </FilterPopoverSubMenu>
            )}

            {isOpen && openPart === "operator" && (
                <FilterBarValueOperatorMenu
                    open
                    submenuAnchorRef={containerRef.current}
                    placement={submenuPlacement}
                    filterOptionType={filterOption.type}
                    filterOperator={filterOption.filterOperator}
                    onChange={(operator) => {
                        onSave({ ...filterOption, filterOperator: operator }, filterBarData);
                    }}
                    onBackdropClick={() => onSave(filterOption, filterBarData)}
                />
            )}
        </>
    );
}

export default FilterBarValue;
